import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import RequireSchoolModal from './dashboard/school/RequireSchoolModal';
import SchoolSelector from './dashboard/school/SchoolSelector';
import { School, LogOut } from 'lucide-react';
import { signOut } from 'firebase/auth';

interface RequireSchoolProps {
  children: React.ReactNode;
}

export default function RequireSchool({ children }: RequireSchoolProps) {
  const { user } = useAuth();
  const { selectedSchool, loading: schoolLoading } = useSchool();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<'admin' | 'staff' | null>(null);

  useEffect(() => {
    const loadUserRole = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const role = userDoc.data().role as 'admin' | 'staff';
          setUserRole(role);
        }
      } catch (error) {
        console.error('Error loading user role:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserRole();
  }, [user]);

  // Don't block these paths
  const allowedPaths = [
    '/onboarding',
    '/profile', 
    '/settings', 
    '/settings/admin',
    '/settings/notifications',
    '/settings/lms'
  ];
  
  if (allowedPaths.includes(location.pathname)) {
    return <>{children}</>;
  }

  if (loading || schoolLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  // If admin with no school selected, show the create school modal
  if (userRole === 'admin' && !selectedSchool) {
    return <RequireSchoolModal />;
  }

  // If user has a school selected (auto-selected or manually selected), allow access
  if (selectedSchool) {
    return <>{children}</>;
  }

  // If staff member doesn't have a school selected, show selector
  if (userRole === 'staff' && !selectedSchool) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-surface-light rounded-xl shadow-lg animate-slide-up">
          <div className="p-6 border-b border-border-light text-center">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <School className="w-8 h-8 text-primary-500" />
            </div>
            <h2 className="text-2xl font-semibold text-text-primary">Join a School</h2>
            <p className="text-text-secondary mt-2">
              Join your school using the school code
            </p>
            <button
              onClick={() => signOut(auth)}
              className="mt-4 flex items-center gap-2 mx-auto px-4 py-2 text-sm text-text-secondary hover:text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Switch Account
            </button>
          </div>

          <SchoolSelector onClose={() => {}} />
        </div>
      </div>
    );
  }

  // Default to showing children if none of the above conditions are met
  return <>{children}</>;
}